import { defineMessage } from 'react-intl';

/**
 * Intl messages for the fragment + composition social publish flows (shared copy, es-AR defaults).
 */
export const publishMessages = {
  waitingPublicLink: defineMessage({
    id: 'socialExport.publish.waitingPublicLink',
    defaultMessage: 'Esperando link público',
  }),
  viewVideo: defineMessage({
    id: 'socialExport.publish.viewVideo',
    defaultMessage: 'Ver video',
  }),
  editionWithName: defineMessage({
    id: 'socialExport.publish.editionWithName',
    defaultMessage: 'Edit: {name}',
  }),
  videoDeletedFromPlatform: defineMessage({
    id: 'socialExport.publish.videoDeletedFromPlatform',
    defaultMessage: 'Video eliminado de la plataforma',
  }),
  errorDeletingVideo: defineMessage({
    id: 'socialExport.publish.errorDeletingVideo',
    defaultMessage: 'Ocurrió un error al eliminar el video',
  }),
  publicationStarted: defineMessage({
    id: 'socialExport.publish.publicationStarted',
    defaultMessage: 'Publicación iniciada',
  }),
  errorPublishingNow: defineMessage({
    id: 'socialExport.publish.errorPublishingNow',
    defaultMessage: 'Ocurrió un error al publicar inmediatamente',
  }),
  noAdditionalActions: defineMessage({
    id: 'socialExport.publish.noAdditionalActions',
    defaultMessage: 'La plataforma no permite ninguna acción adicional',
  }),
  manageVideo: defineMessage({
    id: 'socialExport.publish.manageVideo',
    defaultMessage: 'Administrar video en {platform}',
  }),
  unsavedChangesLeavePrompt: defineMessage({
    id: 'socialExport.form.unsavedChangesLeavePrompt',
    defaultMessage:
      '¿Estás seguro de que quieres salir? Los cambios no guardados se perderán.',
  }),
  deleteFromPlatform: defineMessage({
    id: 'socialExport.publish.deleteFromPlatform',
    defaultMessage: 'Eliminar de {platform}',
  }),
  publishNowConfirmTitle: defineMessage({
    id: 'socialExport.publish.publishNowConfirmTitle',
    defaultMessage: 'Publicar ahora',
  }),
  publishNowConfirmMessage: defineMessage({
    id: 'socialExport.publish.publishNowConfirmMessage',
    defaultMessage:
      '¿Estás seguro de que deseas publicar este video ahora en lugar de esperar a la fecha programada?',
  }),
  publishNow: defineMessage({
    id: 'socialExport.publish.publishNow',
    defaultMessage: 'Publicar ahora',
  }),
  deleteScheduledPublishTitle: defineMessage({
    id: 'socialExport.publish.deleteScheduledPublishTitle',
    defaultMessage: 'Eliminar publicación programada',
  }),
  deleteScheduledPublishMessage: defineMessage({
    id: 'socialExport.publish.deleteScheduledPublishMessage',
    defaultMessage:
      '¿Estás seguro de que deseas eliminar esta publicación programada? Esta acción no se puede deshacer.',
  }),
  retry: defineMessage({
    id: 'socialExport.publish.retry',
    defaultMessage: 'Reintentar',
  }),
  deleteVideoFromPlatformTitle: defineMessage({
    id: 'socialExport.publish.deleteVideoFromPlatformTitle',
    defaultMessage: 'Eliminar video de {platform}',
  }),
  deleteVideoFromPlatformMessage: defineMessage({
    id: 'socialExport.publish.deleteVideoFromPlatformMessage',
    defaultMessage:
      '¿Estás seguro de que deseas eliminar este video de la plataforma? Esta acción no se puede deshacer.',
  }),
  chipPublished: defineMessage({
    id: 'socialExport.publish.chip.published',
    defaultMessage: 'Publicado',
  }),
  chipScheduled: defineMessage({
    id: 'socialExport.publish.chip.scheduled',
    defaultMessage: 'Programado',
  }),
  chipInProgress: defineMessage({
    id: 'socialExport.publish.chip.inProgress',
    defaultMessage: 'En curso',
  }),
  chipWaitingForMedia: defineMessage({
    id: 'socialExport.publish.chip.waitingForMedia',
    defaultMessage: 'Esperando media',
  }),
  chipError: defineMessage({
    id: 'socialExport.publish.chip.error',
    defaultMessage: 'Error',
  }),
  /** Error code line inside the failed-upload details tooltip. */
  chipErrorCode: defineMessage({
    id: 'socialExport.publish.chip.errorCode',
    defaultMessage: 'Código: {errorCode}',
  }),
  chipCancelled: defineMessage({
    id: 'socialExport.publish.chip.cancelled',
    defaultMessage: 'Cancelado',
  }),
  /** Success alert title on upload detail (distinct from compact chip label). */
  uploadDetailPublishedTitle: defineMessage({
    id: 'socialExport.publish.uploadDetail.publishedTitle',
    defaultMessage: 'Video publicado correctamente',
  }),
  sectionTitle: defineMessage({
    id: 'socialExport.publish.sectionTitle',
    defaultMessage: 'Redes Sociales',
  }),
  /** Title above the platform icon bar on the publish tab. */
  publishToSectionTitle: defineMessage({
    id: 'socialExport.publish.publishToSectionTitle',
    defaultMessage: 'Publicar en',
  }),
  /** Title above the list of existing social publications. */
  publicationsSectionTitle: defineMessage({
    id: 'socialExport.publish.publicationsSectionTitle',
    defaultMessage: 'Publicaciones ({count})',
  }),
  /** Label above the platform switcher on the upload page. */
  platformSwitcherLabel: defineMessage({
    id: 'socialExport.publish.platformSwitcherLabel',
    defaultMessage: 'Plataforma',
  }),
  publishTo: defineMessage({
    id: 'socialExport.publish.publishTo',
    defaultMessage: 'Publicar en',
  }),
  /** Upload page title: stays fixed because the platform is picked inside the page. */
  publishToSocialNetworks: defineMessage({
    id: 'socialExport.publish.publishToSocialNetworks',
    defaultMessage: 'Publicar en redes',
  }),
  panoramaNotPublishedFragment: defineMessage({
    id: 'socialExport.publish.panorama.notPublishedFragment',
    defaultMessage: 'Todavía no publicaste este fragmento en redes sociales.',
  }),
  panoramaNotPublishedComposition: defineMessage({
    id: 'socialExport.publish.panorama.notPublishedComposition',
    defaultMessage: 'Todavía no publicaste esta composición en redes sociales.',
  }),
  panoramaNoConnectedPlatforms: defineMessage({
    id: 'socialExport.publish.panorama.noConnectedPlatforms',
    defaultMessage: 'No hay plataformas conectadas para publicar',
  }),
  panoramaNoSourcesFragment: defineMessage({
    id: 'socialExport.publish.panorama.noSourcesFragment',
    defaultMessage:
      'Para publicar en redes sociales es necesario generar un video a partir del fragmento.',
  }),
  panoramaNoSourcesComposition: defineMessage({
    id: 'socialExport.publish.panorama.noSourcesComposition',
    defaultMessage:
      'Para publicar en redes sociales es necesario generar un video a partir de un edit.',
  }),
  panoramaGoToEdits: defineMessage({
    id: 'socialExport.publish.panorama.goToEdits',
    defaultMessage: 'Ir a edits',
  }),
  mediaSelectorEdition: defineMessage({
    id: 'socialExport.publish.mediaSelector.edition',
    defaultMessage: 'Edit',
  }),
  mediaSelectorLabel: defineMessage({
    id: 'socialExport.publish.mediaSelector.label',
    defaultMessage: 'Video a publicar',
  }),
  mediaSelectorOriginalMedia: defineMessage({
    id: 'socialExport.publish.mediaSelector.originalMedia',
    defaultMessage: 'Media original',
  }),
  mediaSelectorRenderNotReadyFragment: defineMessage({
    id: 'socialExport.publish.mediaSelector.renderNotReadyFragment',
    defaultMessage: 'El video de este edit aún no está generado. Generalo desde la pestaña edits.',
  }),
  mediaSelectorRenderNotReadyComposition: defineMessage({
    id: 'socialExport.publish.mediaSelector.renderNotReadyComposition',
    defaultMessage: 'El video de este edit aún no está generado. Generalo desde la pestaña edits.',
  }),
  startPublicationError: defineMessage({
    id: 'socialExport.publish.startPublicationError',
    defaultMessage: 'Ocurrió un error al iniciar la publicación; por favor contactá a soporte',
  }),
  noRenderedEdit: defineMessage({
    id: 'socialExport.publish.noRenderedEdit',
    defaultMessage: 'No hay ningún edit con video generado para publicar.',
  }),
  /** Shown when the user submits before station↔platform connections have finished loading. */
  connectionNotReady: defineMessage({
    id: 'socialExport.publish.connectionNotReady',
    defaultMessage:
      'Todavía se está cargando la conexión con la red social; intentá de nuevo en un momento.',
  }),
  /** Shown when publishing a video-editor edit requires an OAuth connection but none is connected for this station. */
  noOAuthConnectionForPlatform: defineMessage({
    id: 'socialExport.publish.noOAuthConnectionForPlatform',
    defaultMessage: '{platformName} no está conectado para esta estación.',
  }),
  noStation: defineMessage({
    id: 'socialExport.publish.noStation',
    defaultMessage: 'La composición no tiene estación asignada.',
  }),
  noAllowedSourceError: defineMessage({
    id: 'socialExport.publish.noAllowedSourceError',
    defaultMessage: 'No hay ninguna fuente disponible para publicar en esta estación.',
  }),
  videoPreviewTitle: defineMessage({
    id: 'socialExport.publish.videoPreview.title',
    defaultMessage: 'Vista previa del video',
  }),
  createdBy: defineMessage({
    id: 'socialExport.publish.createdBy',
    defaultMessage: 'Creado por {user}',
  }),
  publishedOn: defineMessage({
    id: 'socialExport.publish.publishedOn',
    defaultMessage: 'Publicado el:',
  }),
  publicationDeleted: defineMessage({
    id: 'socialExport.publish.publicationDeleted',
    defaultMessage: 'Publicación eliminada',
  }),
  errorDeletingPublication: defineMessage({
    id: 'socialExport.publish.errorDeletingPublication',
    defaultMessage: 'Ocurrió un error al eliminar la publicación',
  }),
  scheduledAlertTitle: defineMessage({
    id: 'socialExport.publish.scheduledAlertTitle',
    defaultMessage: 'Publicación programada',
  }),
  videoWillPublishAt: defineMessage({
    id: 'socialExport.publish.videoWillPublishAt',
    defaultMessage: 'El video se publicará el:',
  }),
  postPublishProcessingNotice: defineMessage({
    id: 'socialExport.publish.postPublishProcessingNotice',
    defaultMessage:
      'Luego de finalizar la publicación, puede demorar unos minutos en procesarse el contenido y verse en tu perfil.',
  }),
  noPlatformsToPublish: defineMessage({
    id: 'socialExport.publish.noPlatformsToPublish',
    defaultMessage: 'No hay plataformas para publicar',
  }),
  /** Primary submit CTA on publish forms — includes destination so the user confirms the platform. */
  sendToPlatform: defineMessage({
    id: 'socialExport.publish.sendToPlatform',
    defaultMessage: 'Publicar en {platformName}',
  }),
  /** Descriptions for the toggle rows of the publish forms (SwitchCardField). */
  forKidsDescription: defineMessage({
    id: 'socialExport.publish.forKidsDescription',
    defaultMessage: 'Se aplican las restricciones de contenido para menores de la plataforma.',
  }),
  uploadAsPrivateDescription: defineMessage({
    id: 'socialExport.publish.uploadAsPrivateDescription',
    defaultMessage:
      'El video queda visible solo en la cuenta hasta que sea publicado públicamente',
  }),
  shareToFeedDescription: defineMessage({
    id: 'socialExport.publish.shareToFeedDescription',
    defaultMessage: 'Además del reel, la publicación aparecerá en el feed principal de la cuenta.',
  }),
  publishImmediatelyDescription: defineMessage({
    id: 'socialExport.publish.publishImmediatelyDescription',
    defaultMessage: 'Desactivar para elegir una fecha y hora de publicación futura',
  }),
};

/** Alias for upload card + overflow menu (historical grouping). */
export const publishCardMessages = {
  waitingPublicLink: publishMessages.waitingPublicLink,
  viewVideo: publishMessages.viewVideo,
  editionWithName: publishMessages.editionWithName,
  videoDeletedFromPlatform: publishMessages.videoDeletedFromPlatform,
  errorDeletingVideo: publishMessages.errorDeletingVideo,
  publicationStarted: publishMessages.publicationStarted,
  errorPublishingNow: publishMessages.errorPublishingNow,
  noAdditionalActions: publishMessages.noAdditionalActions,
  manageVideo: publishMessages.manageVideo,
  deleteFromPlatform: publishMessages.deleteFromPlatform,
  publishNowConfirmTitle: publishMessages.publishNowConfirmTitle,
  publishNowConfirmMessage: publishMessages.publishNowConfirmMessage,
  publishNow: publishMessages.publishNow,
  deleteScheduledPublishTitle: publishMessages.deleteScheduledPublishTitle,
  deleteScheduledPublishMessage: publishMessages.deleteScheduledPublishMessage,
  retry: publishMessages.retry,
  deleteVideoFromPlatformTitle: publishMessages.deleteVideoFromPlatformTitle,
  deleteVideoFromPlatformMessage: publishMessages.deleteVideoFromPlatformMessage,
};
